/**
 * Layout da home «Experiências» (cliente): limites, secções visíveis e estilo dos cartões.
 * Fonte de verdade: API (DynamoDB) via `PUT /api/admin/cliente-home-layout`; o cliente hidrata a partir de
 * `GET /api/catalogo/cliente` (`clienteHomeLayout`) e espelha em `CLIENTE_HOME_LAYOUT_STORAGE_KEY`.
 */

export const CLIENTE_HOME_LAYOUT_STORAGE_KEY = 'naviex-cliente-home-layout-v1';

export const HOME_LAYOUT_UPDATED_EVENT = 'naviex-home-layout-updated';

/** Estilo dos cartões em Destaques (strip horizontal / carrossel). */
export type DestaquesCardStripSlide = 'fill' | 'default';
export type DestaquesCardBadgeAlign = 'withTitle' | 'topLeft';

export type ClienteHomeLimits = {
  /** Pool máximo para «Ver todos» em Destaques (e feed interno). */
  destaquesPoolMax: number;
  /** Quantos slides no carrossel da home em Destaques. */
  destaquesCarouselMax: number;
  maxExperienciasExpLugares: number;
  maxLugaresExpLugares: number;
  /** Home sem viagem: notícias mais recentes. */
  noticiasSemViagemTotalMax: number;
  noticiasSemViagemComImagem: number;
  /** Com viagem: matérias Destaque Naviex (`pickHomeNewsDestaqueNaviexFeed`). */
  noticiasComViagemTotalMax: number;
  noticiasComViagemComImagem: number;
  noticiasComViagemTexto: number;
  maxEventos: number;
  maxHospedagens: number;
  maxExcursões: number;
  /** Carrossel horizontal «Excursões» na home (1–10). */
  excursõesCarouselMax: number;
};

export type ClienteHomeSectionsNoTrip = {
  destaques: boolean;
  roteiroBanner: boolean;
  experienciasLugares: boolean;
  noticias: boolean;
  eventos: boolean;
  hospedagem: boolean;
  excursões: boolean;
};

export type ClienteHomeSectionsComTrip = {
  buscaNoticias: boolean;
  headlinesNoticias: boolean;
  /** Carrossel «Destaques» (experiências + lugares), igual à home sem viagem. */
  destaques: boolean;
  eventos: boolean;
  hospedagem: boolean;
  excursões: boolean;
  roteiroBannerFinal: boolean;
};

export type ClienteHomeCardStyles = {
  destaquesStripSlide: DestaquesCardStripSlide;
  destaquesBadgeAlign: DestaquesCardBadgeAlign;
};

export type ClienteHomeLayoutSettings = {
  version: 1;
  limits: ClienteHomeLimits;
  sectionsNoTrip: ClienteHomeSectionsNoTrip;
  sectionsComTrip: ClienteHomeSectionsComTrip;
  cardStyles: ClienteHomeCardStyles;
};

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** Valor numérico do layout ou default quando a chave falta / não é finito (evita NaN→0 em limites com min 0). */
function pickLimit(L: Record<string, unknown>, key: string, fallback: number): number {
  const v = L[key];
  if (v === undefined || v === null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function defaultClienteHomeLayoutSettings(): ClienteHomeLayoutSettings {
  return {
    version: 1,
    limits: {
      destaquesPoolMax: 16,
      destaquesCarouselMax: 8,
      maxExperienciasExpLugares: 8,
      maxLugaresExpLugares: 8,
      noticiasSemViagemTotalMax: 10,
      noticiasSemViagemComImagem: 4,
      noticiasComViagemTotalMax: 10,
      noticiasComViagemComImagem: 4,
      noticiasComViagemTexto: 6,
      maxEventos: 8,
      maxHospedagens: 8,
      maxExcursões: 8,
      excursõesCarouselMax: 10,
    },
    sectionsNoTrip: {
      destaques: true,
      roteiroBanner: true,
      experienciasLugares: true,
      noticias: true,
      eventos: true,
      hospedagem: true,
      excursões: true,
    },
    sectionsComTrip: {
      buscaNoticias: true,
      headlinesNoticias: true,
      destaques: true,
      eventos: true,
      hospedagem: true,
      excursões: true,
      roteiroBannerFinal: true,
    },
    cardStyles: {
      destaquesStripSlide: 'fill',
      destaquesBadgeAlign: 'withTitle',
    },
  };
}

export function normalizeClienteHomeLayoutSettings(raw: unknown): ClienteHomeLayoutSettings {
  const d = defaultClienteHomeLayoutSettings();
  if (!raw || typeof raw !== 'object') return d;
  const o = raw as Record<string, unknown>;

  const L = (o.limits && typeof o.limits === 'object' ? o.limits : {}) as Record<string, unknown>;
  d.limits = {
    destaquesPoolMax: clamp(pickLimit(L, 'destaquesPoolMax', d.limits.destaquesPoolMax), 1, 48),
    destaquesCarouselMax: clamp(pickLimit(L, 'destaquesCarouselMax', d.limits.destaquesCarouselMax), 1, 24),
    maxExperienciasExpLugares: clamp(pickLimit(L, 'maxExperienciasExpLugares', d.limits.maxExperienciasExpLugares), 0, 32),
    maxLugaresExpLugares: clamp(pickLimit(L, 'maxLugaresExpLugares', d.limits.maxLugaresExpLugares), 0, 32),
    noticiasSemViagemTotalMax: clamp(pickLimit(L, 'noticiasSemViagemTotalMax', d.limits.noticiasSemViagemTotalMax), 1, 30),
    noticiasSemViagemComImagem: clamp(pickLimit(L, 'noticiasSemViagemComImagem', d.limits.noticiasSemViagemComImagem), 0, 20),
    noticiasComViagemTotalMax: clamp(pickLimit(L, 'noticiasComViagemTotalMax', d.limits.noticiasComViagemTotalMax), 1, 30),
    noticiasComViagemComImagem: clamp(pickLimit(L, 'noticiasComViagemComImagem', d.limits.noticiasComViagemComImagem), 0, 20),
    noticiasComViagemTexto: clamp(pickLimit(L, 'noticiasComViagemTexto', d.limits.noticiasComViagemTexto), 0, 20),
    maxEventos: clamp(pickLimit(L, 'maxEventos', d.limits.maxEventos), 0, 40),
    maxHospedagens: clamp(pickLimit(L, 'maxHospedagens', d.limits.maxHospedagens), 0, 40),
    maxExcursões: clamp(pickLimit(L, 'maxExcursões', d.limits.maxExcursões), 0, 40),
    excursõesCarouselMax: clamp(
      pickLimit(L, 'excursõesCarouselMax', pickLimit(L, 'maxExcursões', d.limits.excursõesCarouselMax)),
      1,
      10
    ),
  };

  const sn = (o.sectionsNoTrip && typeof o.sectionsNoTrip === 'object' ? o.sectionsNoTrip : {}) as Record<string, unknown>;
  d.sectionsNoTrip = {
    destaques: sn.destaques !== false,
    roteiroBanner: sn.roteiroBanner !== false,
    experienciasLugares: sn.experienciasLugares !== false,
    noticias: sn.noticias !== false,
    eventos: sn.eventos !== false,
    hospedagem: sn.hospedagem !== false,
    excursões: sn.excursões !== false,
  };

  const st = (o.sectionsComTrip && typeof o.sectionsComTrip === 'object' ? o.sectionsComTrip : {}) as Record<string, unknown>;
  d.sectionsComTrip = {
    buscaNoticias: st.buscaNoticias !== false,
    headlinesNoticias: st.headlinesNoticias !== false,
    destaques: st.destaques !== false,
    eventos: st.eventos !== false,
    hospedagem: st.hospedagem !== false,
    excursões: st.excursões !== false,
    roteiroBannerFinal: st.roteiroBannerFinal !== false,
  };

  const cs = (o.cardStyles && typeof o.cardStyles === 'object' ? o.cardStyles : {}) as Record<string, unknown>;
  const slide = cs.destaquesStripSlide === 'default' ? 'default' : 'fill';
  const badge = cs.destaquesBadgeAlign === 'topLeft' ? 'topLeft' : 'withTitle';
  d.cardStyles = { destaquesStripSlide: slide, destaquesBadgeAlign: badge };

  if (d.limits.noticiasSemViagemComImagem > d.limits.noticiasSemViagemTotalMax) {
    d.limits.noticiasSemViagemComImagem = d.limits.noticiasSemViagemTotalMax;
  }
  if (d.limits.destaquesCarouselMax > d.limits.destaquesPoolMax) {
    d.limits.destaquesCarouselMax = d.limits.destaquesPoolMax;
  }

  return d;
}

export function parseClienteHomeLayoutSettingsJson(json: string): ClienteHomeLayoutSettings {
  try {
    return normalizeClienteHomeLayoutSettings(JSON.parse(json));
  } catch {
    return defaultClienteHomeLayoutSettings();
  }
}
